import { KBoSettingsTabs } from "components/Admin/Organizations/OrganizationDetail/OrganizationKBoSettings/KBoSettingsTabs";
import {
    Alert,
    AlertTitle,
    Grid,
    styled,
    Tooltip,
    Typography,
} from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import AcxToggleButton from "components/UI/AcxToggleButton";
import type {
    KboAvailableModel,
    KboPresetScope,
    KboSkillDefinition,
    KboSettings,
    ToolDefinition,
} from "components/KBo/Models/KboSettings";
import {
    cloneKboSettings,
    cloneToolConfigurations,
    getSkillRequiredToolNames,
    getSkillScopedToolConfigurations,
    mergeToolConfigurations,
    toSparseKboSettings,
} from "components/KBo/Models/KboSettingsHelpers";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AcxAgentService } from "services/AcxAgentService";
import { ServiceError } from "services/BaseService";
import { useStore } from "utils/useStore";
import { KBoCoreConfiguration } from "./KBoCoreConfiguration";
import { KBoSkillToggles } from "./KBoSkillToggles";
import { KBoToolToggles } from "./KBoToolToggles";
import { KBoAnalysisConfiguration } from "./KBoAnalysisConfiguration";
import { KBoAdvancedConfiguration } from "./KBoAdvancedConfiguration";
import {
    TemperatureValidityContext,
    useTemperatureValidity,
} from "components/LLMs/TemperatureField";

const Header = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.gray[700],
    fontSize: "24px",
}));

const TENANT_DEFAULTS_NAME = "Tenant defaults";
const TENANT_DEFAULTS_SCOPE = "tenant-defaults" as const;

type AdminPresetScope = KboPresetScope | typeof TENANT_DEFAULTS_SCOPE;

type AdminPresetSelection = {
    name: string;
    scope: AdminPresetScope;
};

type AdminPresetOption = AdminPresetSelection & {
    label: string;
    value: string;
};

type AdminPresetOptionGroup = {
    label: string;
    options: AdminPresetOption[];
};

const TENANT_DEFAULTS_SELECTION: AdminPresetSelection = {
    name: TENANT_DEFAULTS_NAME,
    scope: TENANT_DEFAULTS_SCOPE,
};

export const OrganizationKBoSettings = observer(() => {
    const [settings, setSettings] = useState<KboSettings>({});
    const [tenantDefaultSettings, setTenantDefaultSettings] =
        useState<KboSettings>({});
    const [globalDefaults, setGlobalDefaults] =
        useState<KboSettings | null>(null);
    const [availableModels, setAvailableModels] = useState<
        KboAvailableModel[]
    >([]);
    const [availableEmbeddingModels, setAvailableEmbeddingModels] = useState<
        string[]
    >([]);
    const [availableSkills, setAvailableSkills] = useState<
        KboSkillDefinition[]
    >([]);
    const [availableTools, setAvailableTools] = useState<ToolDefinition[]>([]);
    const [templateTags, setTemplateTags] = useState<Record<string, string[]>>(
        {},
    );
    const [tenantPresets, setTenantPresets] = useState<
        Record<string, KboSettings>
    >({});
    const [personalPresets, setPersonalPresets] = useState<
        Record<string, KboSettings>
    >({});
    const [selectedPreset, setSelectedPreset] =
        useState<AdminPresetSelection>(TENANT_DEFAULTS_SELECTION);
    const [isSaveNewPresetOpen, setIsSaveNewPresetOpen] = useState(false);
    const [newPresetName, setNewPresetName] = useState("");
    const [newPresetScope, setNewPresetScope] =
        useState<KboPresetScope>("personal");
    const [newPresetError, setNewPresetError] = useState("");
    const [showTenantDefaultsConfirm, setShowTenantDefaultsConfirm] =
        useState(false);
    const [showDeletePresetConfirm, setShowDeletePresetConfirm] =
        useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingNewPreset, setIsSavingNewPreset] = useState(false);
    const [isDeletingPreset, setIsDeletingPreset] = useState(false);
    const [settingsRenderKey, setSettingsRenderKey] = useState(0);

    const service = useMemo(() => new AcxAgentService(), []);
    const messageStore = useStore(MessageStore);
    const { hasInvalidTemperature, registerTemperatureValidity } =
        useTemperatureValidity();

    const loadData = useCallback(
        async (abortSignal?: AbortSignal) => {
            try {
                setIsLoading(true);
                const [
                    orgSettings,
                    models,
                    embeddingModels,
                    skills,
                    tools,
                    defaults,
                    tags,
                    savedTenantPresets,
                    savedPersonalPresets,
                ] = await Promise.all([
                    service.getOrgKboSettings(),
                    service.getAvailableModels(),
                    service.getAvailableEmbeddingModels(),
                    service.getAvailableSkills(),
                    service.getAvailableTools(),
                    service.getGlobalDefaults(),
                    service.getTemplateTags(),
                    service.getTenantPresets(),
                    service.getSavedPresets(),
                ]);
                const resolved = orgSettings ?? {};
                setSettings(cloneKboSettings(resolved));
                setTenantDefaultSettings(cloneKboSettings(resolved));
                setGlobalDefaults(defaults);
                setAvailableModels(models);
                setAvailableEmbeddingModels(embeddingModels);
                setAvailableSkills(skills);
                setAvailableTools(tools);
                setTemplateTags(tags);
                setTenantPresets(savedTenantPresets);
                setPersonalPresets(savedPersonalPresets);
                setSelectedPreset(TENANT_DEFAULTS_SELECTION);
            } catch (error) {
                if (
                    error instanceof Error &&
                    error.name !== "AbortError"
                ) {
                    messageStore.logError(
                        `Failed to load KBo settings: ${error.message}`,
                    );
                }
            } finally {
                if (!abortSignal?.aborted) {
                    setIsLoading(false);
                }
            }
        },
        [service, messageStore],
    );

    const getSettingsToSave = useCallback(() => {
        const {
            activePresetName: _,
            activePresetScope: __,
            ...settingsToSave
        } = cloneKboSettings({
            ...settings,
            version: settings.version ?? 1,
        });
        return {
            ...toSparseKboSettings(settingsToSave, globalDefaults),
            version: settingsToSave.version ?? 1,
        };
    }, [globalDefaults, settings]);

    const saveSelectedPreset = useCallback(async () => {
        if (hasInvalidTemperature) return false;

        try {
            setIsSaving(true);
            const settingsToSave = getSettingsToSave();

            if (selectedPreset.scope === TENANT_DEFAULTS_SCOPE) {
                await service.updateOrgKboSettings(settingsToSave);
                setTenantDefaultSettings(cloneKboSettings(settingsToSave));
                messageStore.logMessage(
                    "Tenant default KBo settings saved",
                    "success",
                );
            } else if (selectedPreset.scope === "tenant") {
                await service.saveTenantPreset(
                    selectedPreset.name,
                    settingsToSave,
                );
                setTenantPresets((previous) => ({
                    ...previous,
                    [selectedPreset.name]: settingsToSave,
                }));
                messageStore.logMessage(
                    `Organization preset "${selectedPreset.name}" saved`,
                    "success",
                );
            } else {
                await service.savePreset(selectedPreset.name, settingsToSave);
                setPersonalPresets((previous) => ({
                    ...previous,
                    [selectedPreset.name]: settingsToSave,
                }));
                messageStore.logMessage(
                    `Personal preset "${selectedPreset.name}" saved`,
                    "success",
                );
            }

            return true;
        } catch (error) {
            if (error instanceof Error) {
                messageStore.logError(
                    `Failed to save KBo settings: ${error.message}`,
                );
            }
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [
        getSettingsToSave,
        hasInvalidTemperature,
        messageStore,
        selectedPreset,
        service,
    ]);

    const handleSave = useCallback(() => {
        if (selectedPreset.scope === TENANT_DEFAULTS_SCOPE) {
            setShowTenantDefaultsConfirm(true);
            return;
        }

        void saveSelectedPreset();
    }, [saveSelectedPreset, selectedPreset.scope]);

    const handleConfirmSaveTenantDefaults = useCallback(async () => {
        if (await saveSelectedPreset()) {
            setShowTenantDefaultsConfirm(false);
        }
    }, [saveSelectedPreset]);

    const handleResetToDefaults = useCallback(() => {
        setSettings({});
        setSettingsRenderKey((key) => key + 1);
    }, []);

    const handleLoadPreset = useCallback(
        (selection: AdminPresetSelection) => {
            const preset =
                selection.scope === TENANT_DEFAULTS_SCOPE
                    ? tenantDefaultSettings
                    : selection.scope === "tenant"
                      ? tenantPresets[selection.name]
                      : personalPresets[selection.name];
            if (!preset) return;

            const {
                activePresetName: _,
                activePresetScope: __,
                ...presetSettings
            } = cloneKboSettings(preset);
            setSettings(presetSettings);
            setSelectedPreset(selection);
            setSettingsRenderKey((key) => key + 1);
        },
        [personalPresets, tenantDefaultSettings, tenantPresets],
    );

    const handleOpenSaveNewPreset = useCallback(() => {
        setNewPresetName("");
        setNewPresetScope("personal");
        setNewPresetError("");
        setIsSaveNewPresetOpen(true);
    }, []);

    const handleCloseSaveNewPreset = useCallback(() => {
        if (isSavingNewPreset) return;
        setIsSaveNewPresetOpen(false);
        setNewPresetError("");
    }, [isSavingNewPreset]);

    const handleSaveNewPreset = useCallback(async () => {
        const name = newPresetName.trim();
        if (!name) {
            setNewPresetError("Preset name is required.");
            return;
        }
        if (
            name.localeCompare(TENANT_DEFAULTS_NAME, undefined, {
                sensitivity: "accent",
            }) === 0
        ) {
            setNewPresetError(
                `"${TENANT_DEFAULTS_NAME}" is reserved and cannot be used as a preset name.`,
            );
            return;
        }

        const scopedPresets =
            newPresetScope === "tenant" ? tenantPresets : personalPresets;
        const duplicateName = Object.keys(scopedPresets).some(
            (existingName) =>
                existingName.localeCompare(name, undefined, {
                    sensitivity: "accent",
                }) === 0,
        );
        if (duplicateName) {
            setNewPresetError(
                `A ${newPresetScope === "tenant" ? "organization" : "personal"} preset named "${name}" already exists.`,
            );
            return;
        }

        try {
            setIsSavingNewPreset(true);
            setNewPresetError("");
            const settingsToSave = getSettingsToSave();
            if (newPresetScope === "tenant") {
                await service.createTenantPreset(name, settingsToSave);
                setTenantPresets((previous) => ({
                    ...previous,
                    [name]: settingsToSave,
                }));
            } else {
                await service.createPreset(name, settingsToSave);
                setPersonalPresets((previous) => ({
                    ...previous,
                    [name]: settingsToSave,
                }));
            }

            setSelectedPreset({ name, scope: newPresetScope });
            setIsSaveNewPresetOpen(false);
            messageStore.logMessage(`Preset "${name}" saved`, "success");
        } catch (error) {
            setNewPresetError(
                error instanceof ServiceError
                    ? (error.errorMessage ?? error.message)
                    : error instanceof Error
                      ? error.message
                      : "Failed to save the preset.",
            );
        } finally {
            setIsSavingNewPreset(false);
        }
    }, [
        getSettingsToSave,
        messageStore,
        newPresetName,
        newPresetScope,
        personalPresets,
        service,
        tenantPresets,
    ]);

    const handleDeletePreset = useCallback(async () => {
        if (selectedPreset.scope === TENANT_DEFAULTS_SCOPE) return;

        const { name, scope } = selectedPreset;
        try {
            setIsDeletingPreset(true);
            if (scope === "tenant") {
                await service.deleteTenantPreset(name);
                setTenantPresets((previous) => {
                    const next = { ...previous };
                    delete next[name];
                    return next;
                });
            } else {
                await service.deletePreset(name);
                setPersonalPresets((previous) => {
                    const next = { ...previous };
                    delete next[name];
                    return next;
                });
            }

            handleLoadPreset(TENANT_DEFAULTS_SELECTION);
            setShowDeletePresetConfirm(false);
            messageStore.logMessage(`Preset "${name}" deleted`, "success");
        } catch (error) {
            if (error instanceof Error) {
                messageStore.logError(
                    `Failed to delete preset: ${error.message}`,
                );
            }
        } finally {
            setIsDeletingPreset(false);
        }
    }, [handleLoadPreset, messageStore, selectedPreset, service]);

    useEffect(() => {
        const abortController = new AbortController();
        loadData(abortController.signal);
        return () => abortController.abort();
    }, [loadData]);

    const updateSettings = useCallback(
        (patch: Partial<KboSettings>) => {
            setSettings((prev) => ({ ...prev, ...patch }));
        },
        [],
    );

    const onSelectedSkillNamesChange = useCallback(
        (selectedSkillNames: string[]) => {
            const defaultToolConfigurations = cloneToolConfigurations(
                globalDefaults?.toolConfigurations,
            );
            const baseToolConfigurations = mergeToolConfigurations(
                defaultToolConfigurations,
                settings.toolConfigurations,
            );

            updateSettings({
                selectedSkillNames,
                toolConfigurations: getSkillScopedToolConfigurations(
                    baseToolConfigurations,
                    availableTools,
                    availableSkills,
                    selectedSkillNames,
                ),
            });
        },
        [
            availableSkills,
            availableTools,
            globalDefaults?.toolConfigurations,
            settings.toolConfigurations,
            updateSettings,
        ],
    );

    const onToolConfigurationsChange = useCallback(
        (toolConfigurations: KboSettings["toolConfigurations"]) =>
            updateSettings({ toolConfigurations }),
        [updateSettings],
    );

    const effectiveSelectedSkillNames =
        settings.selectedSkillNames ?? globalDefaults?.selectedSkillNames;

    const effectiveToolConfigurations = useMemo(
        () => {
            const baseToolConfigurations = mergeToolConfigurations(
                globalDefaults?.toolConfigurations,
                settings.toolConfigurations,
            );

            return getSkillScopedToolConfigurations(
                baseToolConfigurations,
                availableTools,
                availableSkills,
                effectiveSelectedSkillNames,
            );
        },
        [
            availableSkills,
            availableTools,
            effectiveSelectedSkillNames,
            settings.toolConfigurations,
            globalDefaults?.toolConfigurations,
        ],
    );

    const skillRequiredToolNames = useMemo(
        () =>
            getSkillRequiredToolNames(
                availableSkills,
                effectiveSelectedSkillNames,
            ),
        [availableSkills, effectiveSelectedSkillNames],
    );

    const presetOptions = useMemo<AdminPresetOptionGroup[]>(
        () => [
            {
                label: "Organization",
                options: [
                    {
                        ...TENANT_DEFAULTS_SELECTION,
                        label: TENANT_DEFAULTS_NAME,
                        value: TENANT_DEFAULTS_SCOPE,
                    },
                    ...Object.keys(tenantPresets).map((name) => ({
                        label: name,
                        value: `tenant:${encodeURIComponent(name)}`,
                        name,
                        scope: "tenant" as const,
                    })),
                ],
            },
            {
                label: "Personal",
                options: Object.keys(personalPresets).map((name) => ({
                    label: name,
                    value: `personal:${encodeURIComponent(name)}`,
                    name,
                    scope: "personal" as const,
                })),
            },
        ],
        [personalPresets, tenantPresets],
    );

    const selectedPresetOption = useMemo(
        () =>
            presetOptions
                .flatMap((group) => group.options)
                .find(
                    (option) =>
                        option.name === selectedPreset.name &&
                        option.scope === selectedPreset.scope,
                ) ?? null,
        [presetOptions, selectedPreset],
    );

    if (isLoading) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                paddingBlock={10}
            >
                <AcxLoadingIndicator alternate="PuffLoader" size={64} />
            </Grid>
        );
    }

    return (
        <TemperatureValidityContext.Provider
            value={registerTemperatureValidity}
        >
            <Grid
                container
                direction="column"
                rowGap={3}
                paddingBlock={5}
                paddingInline={10}
            >
                <Grid
                    container
                    item
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="nowrap"
                    columnGap={2}
                >
                    <Grid item>
                        <Header>KBo Settings</Header>
                    </Grid>
                    <Grid
                        item
                        container
                        columnGap={1}
                        justifyContent="flex-end"
                    >
                        <Grid item>
                            <AcxButton
                                color="white"
                                onClick={handleResetToDefaults}
                                disabled={!globalDefaults}
                            >
                                Reset to Global Defaults
                            </AcxButton>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid
                    item
                    container
                    alignItems="center"
                    columnGap={2}
                    rowGap={1}
                    sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 2,
                    }}
                >
                    <Grid item xs minWidth={220}>
                        <Typography fontWeight={600}>KBo Presets</Typography>
                    </Grid>
                    <Grid item sx={{ minWidth: 240 }}>
                        <AcxSelect
                            id="kbo-preset-selector"
                            options={presetOptions}
                            defaultValue={selectedPresetOption}
                            onChange={(option: AdminPresetOption) => {
                                handleLoadPreset(option);
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <Tooltip
                            title={
                                hasInvalidTemperature
                                    ? "Fix the out-of-range temperature before saving."
                                    : ""
                            }
                        >
                            <span>
                                <AcxButton
                                    loading={isSaving}
                                    disabled={hasInvalidTemperature}
                                    onClick={handleSave}
                                >
                                    Save
                                </AcxButton>
                            </span>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <AcxButton
                            color="white"
                            disabled={hasInvalidTemperature}
                            onClick={handleOpenSaveNewPreset}
                        >
                            Save New Preset
                        </AcxButton>
                    </Grid>
                    <Grid item>
                        <AcxButton
                            color="white"
                            loading={isDeletingPreset}
                            disabled={
                                selectedPreset.scope === TENANT_DEFAULTS_SCOPE
                            }
                            onClick={() => setShowDeletePresetConfirm(true)}
                        >
                            Delete Preset
                        </AcxButton>
                    </Grid>
                </Grid>

                <Grid item>
                    <Alert
                        severity="warning"
                        sx={{
                            fontWeight: 600,
                            alignItems: "center",
                            "& .MuiAlert-message": { width: "100%" },
                        }}
                    >
                        <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
                            Advanced settings
                        </AlertTitle>
                        These settings override how KBo behaves. Incorrect
                        values can degrade answer quality, increase cost, or
                        break KBo entirely. Only change a setting if you
                        understand exactly what it does.
                    </Alert>
                </Grid>

                <Grid item>
                    <KBoSettingsTabs resetKey={settingsRenderKey}>
                        <KBoCoreConfiguration
                            systemPrompt={settings.systemPrompt ?? undefined}
                            prompts={settings.prompts ?? undefined}
                            onChange={updateSettings}
                            defaults={globalDefaults}
                            templateTags={templateTags}
                        />

                        <Grid container direction="column" rowGap={3}>
                            <KBoSkillToggles
                                selectedSkillNames={settings.selectedSkillNames}
                                availableSkills={availableSkills}
                                defaults={globalDefaults}
                                onChange={onSelectedSkillNamesChange}
                            />
                            <KBoToolToggles
                                namespaceDescriptions={{
                                    ...globalDefaults?.toolNamespaceDescriptions,
                                    ...settings.toolNamespaceDescriptions,
                                }}
                                onNamespaceDescriptionsChange={(
                                    toolNamespaceDescriptions,
                                ) =>
                                    updateSettings({
                                        toolNamespaceDescriptions,
                                    })
                                }
                                toolConfigurations={effectiveToolConfigurations}
                                availableTools={availableTools}
                                skillRequiredToolNames={skillRequiredToolNames}
                                onChange={onToolConfigurationsChange}
                            />
                        </Grid>

                        <KBoAnalysisConfiguration
                            analysis={settings.analysis ?? undefined}
                            analysisPrompts={
                                settings.analysisPrompts ?? undefined
                            }
                            availableModels={availableModels}
                            onChange={updateSettings}
                            defaults={globalDefaults}
                            templateTags={templateTags}
                        />

                        <KBoAdvancedConfiguration
                            settings={settings}
                            availableModels={availableModels}
                            availableEmbeddingModels={availableEmbeddingModels}
                            onChange={updateSettings}
                            defaults={globalDefaults}
                        />
                    </KBoSettingsTabs>
                </Grid>
            </Grid>

            <AcxConfirmationDialog
                isOpen={showTenantDefaultsConfirm}
                onClose={() => {
                    if (!isSaving) setShowTenantDefaultsConfirm(false);
                }}
                title="Danger zone: Save Tenant Defaults"
                content="This will replace the tenant's default KBo settings and can affect every user who inherits them. Named presets will not be changed."
                confirmButtonText="Save Tenant Defaults"
                confirmButtonId="confirm-save-tenant-kbo-defaults"
                onConfirm={handleConfirmSaveTenantDefaults}
                isLoading={isSaving}
            />

            <AcxConfirmationDialog
                isOpen={showDeletePresetConfirm}
                onClose={() => {
                    if (!isDeletingPreset) setShowDeletePresetConfirm(false);
                }}
                title="Delete Preset"
                content={`Delete "${selectedPreset.name}"? This cannot be undone. The editor will return to Tenant defaults.`}
                confirmButtonText="Delete Preset"
                confirmButtonId="confirm-delete-kbo-preset"
                onConfirm={handleDeletePreset}
                isLoading={isDeletingPreset}
            />

            <AcxDialog
                title="Save New Preset"
                isOpen={isSaveNewPresetOpen}
                onClose={handleCloseSaveNewPreset}
                contentWidth="400px"
                hideCloseButton
                hideCancelButton
                dialogContentChildren={
                    <Grid container direction="column" rowSpacing={2}>
                        <Grid item>
                            <AcxMainTextField
                                id="kbo-new-preset-name"
                                fullWidth
                                labelText="Preset Name"
                                value={newPresetName}
                                error={Boolean(newPresetError)}
                                helperText={newPresetError || undefined}
                                onChange={(event) => {
                                    setNewPresetName(event.target.value);
                                    setNewPresetError("");
                                }}
                                onKeyDown={(event) => event.stopPropagation()}
                            />
                        </Grid>
                        <Grid item>
                            <Typography fontWeight="bold" sx={{ mb: 1 }}>
                                Scope
                            </Typography>
                            <AcxToggleButton
                                id="kbo-new-preset-scope"
                                exclusive
                                defaultValue={newPresetScope}
                                items={[
                                    {
                                        id: "kbo-new-preset-scope-user",
                                        element: "Only Me",
                                        value: "personal",
                                    },
                                    {
                                        id: "kbo-new-preset-scope-organization",
                                        element: "My Organization",
                                        value: "tenant",
                                    },
                                ]}
                                onChange={(scope?: KboPresetScope) => {
                                    setNewPresetScope(scope ?? "personal");
                                    setNewPresetError("");
                                }}
                                justifyContent="center"
                                groupFlexGrow={1}
                            />
                        </Grid>
                        <Grid
                            container
                            item
                            direction="row"
                            columnSpacing={1}
                            justifyContent="flex-end"
                        >
                            <Grid item>
                                <AcxButton
                                    color="white"
                                    disabled={isSavingNewPreset}
                                    onClick={handleCloseSaveNewPreset}
                                >
                                    Cancel
                                </AcxButton>
                            </Grid>
                            <Grid item>
                                <AcxButton
                                    id="kbo-save-new-preset"
                                    loading={isSavingNewPreset}
                                    disabled={
                                        !newPresetName.trim() ||
                                        hasInvalidTemperature
                                    }
                                    onClick={handleSaveNewPreset}
                                >
                                    Save
                                </AcxButton>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            />
        </TemperatureValidityContext.Provider>
    );
});
